import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { isAfter, isEqual } from 'date-fns'
import { EPaymentMethod, type EPaymentType } from '../types/payment'
import { EBookerLocationType, ELocationPurpose } from '~/data/bookerData'

export interface IBookingStorePaymentModel {
	type: EPaymentType | null
	method: EPaymentMethod | null
}

export interface IBookingRequestVariables {
	dates: {
		start: Date | null
		end: Date | null
	} | null
	locations: {
		start: Partial<ILocationModel> | null
		end: Partial<ILocationModel> | null
	} | null
	otherEndLocation: boolean
	selectedCar: IProductModel | null
	selectedServices: IBookingServiceModel[]
	couponCode: string | null
	additionalDrivers: number
	isCodeRechecked: boolean
}

export interface IBookingStoreState {
	booking: IBookingModel | null
	currentBookingUidCookie: string | null
	requestVariables: IBookingRequestVariables
	isLoading: boolean
	payment: IReservationPaymentModel
}

export interface IBookingStore {
	readonly state: IBookingStoreState
	fetchBooking: () => Promise<IBookingModel | undefined>
	updateBooking: (disableCheckCode?: boolean) => Promise<IBookingModel | undefined>
	setBookerDates: (dates: { start: Date | null; end: Date | null }, source?: EBookerDatesSource) => void
	setBookerLocations: (locations: { start: ILocationModel | null; end: ILocationModel | null }) => void
	setOtherEndLocation: (value: boolean) => void
	setIsLoading: (isLoading: boolean) => void
	setAdditionalDrivers: (additionalDrivers: number) => Promise<IBookingModel | undefined>
	updateSelectedCar: (car: IProductModel | null) => void
	selectInsurance: (insuranceId: number) => Promise<IBookingModel | null | undefined>
	selectService: (service: IBookingServiceModel, serviceToDel?: IBookingServiceModel) => Promise<IBookingModel | undefined>
	unselectService: (service: IBookingServiceModel) => Promise<IBookingModel | undefined>
	selectAbroadZone: (zone: IBookingServiceModel) => Promise<IBookingModel | null | undefined>
	unselectAbroadZones: () => Promise<IBookingModel | null | undefined>
	updatePayment: (payment: IReservationPaymentModel) => void
	updatePaymentType: (type: EPaymentType) => void
	updatePaymentMethod: (method: EPaymentMethod | null) => void
	deleteCurrentBookingUidCookie: () => void
	finalizeBooking: (payload: IBookingStoreFinalizeRequest) => Promise<{ booking: IBookingModel; gateway_url: string } | undefined>
	setDiscountCode: (code: ICouponCodeModel | null, recheck?: boolean, disableCheckCode?: boolean) => void
	calculateDeliveryPrice: (placeID: string, key: EBookerLocationType) => void
	isDatesCorrectForRenteon: (startDate: Date, endDate: Date) => boolean
	getBooking: ComputedRef<IBookingModel | null>
	getLocalBookerDates: ComputedRef<{ start: Date | null; end: Date | null; source: EBookerDatesSource } | null>
	getLocalBookerLocations: ComputedRef<{ start: ILocationModel | null; end: ILocationModel | null } | null>
	getBookingCookie: ComputedRef<string | null>
	getIsStartDateCorrectForRenteon: ComputedRef<boolean>
	getAdditionalDriverService: ComputedRef<IBookingServiceModel | undefined>
	getBookerDates: ComputedRef<{ start: Date | null; end: Date | null } | null>
	getBookerLocations: ComputedRef<{ start: ILocationModel | null; end: ILocationModel | null } | null>
	getIsOtherEndLocation: ComputedRef<boolean>
	getBookingInsurances: ComputedRef<IBookingServiceModel[] | undefined>
	getSelectedCar: ComputedRef<IProductModel | null>
	getAdditionalDrivers: ComputedRef<number>
	getSelectedInsurance: ComputedRef<IBookingServiceModel | undefined>
	getAddonServices: ComputedRef<IBookingServiceModel[]>
	getAbroadZoneServices: ComputedRef<IBookingServiceModel[]>
	getAbroadZoneSelectedService: ComputedRef<IBookingServiceModel | undefined>
	getAbroadZoneSelectedServices: ComputedRef<IBookingServiceModel[]>
	getDays: ComputedRef<number | null>
	getIsLoading: ComputedRef<boolean>
	getCarRentCost: ComputedRef<number | undefined>
	getAdditionalServicesCost: ComputedRef<number>
	getPrepareCost: ComputedRef<number | undefined>
	getPayment: ComputedRef<IBookingStorePaymentModel>
	getPaymentType: ComputedRef<EPaymentType | null>
	getPaymentMethod: ComputedRef<EPaymentMethod | null>
	getRequestVariables: ComputedRef<IBookingRequestVariables>
	getIsCodeRechecked: ComputedRef<boolean>
	setIsCodeRechecked: (state: boolean) => void
}

export const useBookingStore = defineStore('bookingStore', (): IBookingStore => {
	const { toInstant } = useDateTimeService()

	const _state = reactive<IBookingStoreState>({
		booking: null,
		currentBookingUidCookie: null,
		requestVariables: {
			dates: {
				start: null,
				end: null,
			},
			locations: {
				start: null,
				end: null,
			},
			otherEndLocation: false,
			selectedCar: null,
			selectedServices: [],
			couponCode: null,
			additionalDrivers: 0,
			isCodeRechecked: false,
		},
		isLoading: false,
		payment: {
			type: null,
			method: null,
		},
	})

	const notyf = useNotyfService()
	const $t = useI18n().t
	const route = useRoute()
	const router = useRouter()
	const sessionStorageBookingUidToken = 'cf_booking_uuid'
	const sessionStorageBookingLocationsToken = 'cf_booking_location'
	const sessionStorageBookingDatesToken = 'cf_booking_dates'
	const { locale } = useI18n()
	const localePath = useLocalePath()

	const isOnReservationPage = computed(() => {
		return route.name?.toString().includes('reservation')
	})

	const transformServicesForRequest = (services?: IBookingServiceModel[]) => {
		const servicesRequest: Partial<IBookingServiceModel>[] = []

		if (!services) return servicesRequest

		services.forEach((service) => {
			if (hasServiceCode(service, ERenteonServiceCode.ADDITIONAL_DRIVER)) return
			servicesRequest.push({
				serviceID: service.serviceID,
				quantity: service.quantity,
				isSelected: service.isSelected,
			})
		})

		const additionalDriversService = additionalDrivers().additionalDriversService

		if (additionalDriversService) {
			servicesRequest.push(additionalDriversService)
		}

		return servicesRequest.map((service) => BookingServiceToJSON(service))
	}

	const outAndInRequestLocations = (requestVariables: IBookingRequestVariables) => {
		const office_out_id = null
		const office_in_id = null
		const delivery_place_id = null
		const collection_place_id = null

		const startLocation = requestVariables.locations?.start
		const endLocation = requestVariables.locations?.end

		// Start loctation without end location
		if (startLocation && !endLocation) {
			// Start location is delivery
			if (!startLocation.crmID) {
				// Start location is meet and greet
				if (startLocation.purpose === ELocationPurpose.MEET_AND_GREET) {
					return {
						office_out_id: null,
						office_in_id: null,
						delivery_place_id: null,
						collection_place_id: null,
						meet_and_greet_out_id: startLocation.id,
						meet_and_greet_in_id: startLocation.id,
					}
				} else
					return {
						office_out_id: null,
						office_in_id: null,
						delivery_place_id: startLocation.placeID,
						collection_place_id: startLocation.placeID,
						meet_and_greet_out_id: null,
						meet_and_greet_in_id: null,
					}
				// Start location is office
			} else if (startLocation.crmID) {
				return {
					office_out_id: startLocation.crmID,
					office_in_id: startLocation.crmID,
					delivery_place_id: null,
					collection_place_id: null,
					meet_and_greet_out_id: null,
					meet_and_greet_in_id: null,
				}
			}
		} else if (startLocation && endLocation) {
			// Start location is meet and greet
			if (startLocation.purpose === ELocationPurpose.MEET_AND_GREET) {
				// End location is meet and greet
				if (endLocation.purpose === ELocationPurpose.MEET_AND_GREET) {
					return {
						office_out_id: null,
						office_in_id: null,
						delivery_place_id: null,
						collection_place_id: null,
						meet_and_greet_out_id: startLocation.id,
						meet_and_greet_in_id: endLocation.id,
					}
				}
				// End location is office
				else if (!endLocation.crmID) {
					return {
						office_out_id: null,
						office_in_id: null,
						delivery_place_id: startLocation.placeID,
						collection_place_id: endLocation.placeID,
						meet_and_greet_out_id: null,
						meet_and_greet_in_id: null,
					}
					// End location is office
				} else if (endLocation.crmID) {
					return {
						office_out_id: null,
						office_in_id: endLocation.crmID,
						delivery_place_id: startLocation.placeID,
						collection_place_id: null,
						meet_and_greet_out_id: null,
						meet_and_greet_in_id: null,
					}
				}
			}
			// Start location is delivery
			else if (!startLocation.crmID) {
				// End location is meet and greet
				if (endLocation.purpose === ELocationPurpose.MEET_AND_GREET) {
					return {
						office_out_id: null,
						office_in_id: null,
						delivery_place_id: startLocation.placeID,
						collection_place_id: null,
						meet_and_greet_out_id: null,
						meet_and_greet_in_id: endLocation.id,
					}
				}

				// End location is delivery
				else if (!endLocation.crmID) {
					return {
						office_out_id: null,
						office_in_id: null,
						delivery_place_id: startLocation.placeID,
						collection_place_id: endLocation.placeID,
						meet_and_greet_out_id: null,
						meet_and_greet_in_id: null,
					}
					// End location is office
				} else if (endLocation.crmID) {
					return {
						office_out_id: null,
						office_in_id: endLocation.crmID,
						delivery_place_id: startLocation.placeID,
						collection_place_id: null,
						meet_and_greet_out_id: null,
						meet_and_greet_in_id: null,
					}
				}
				// Start location is office
			} else if (startLocation.crmID) {
				// End location is meet and greet
				if (endLocation.purpose === ELocationPurpose.MEET_AND_GREET) {
					return {
						office_out_id: startLocation.crmID,
						office_in_id: null,
						delivery_place_id: null,
						collection_place_id: null,
						meet_and_greet_out_id: null,
						meet_and_greet_in_id: endLocation.id,
					}
				}

				// End location is delivery
				else if (!endLocation.crmID) {
					return {
						office_out_id: startLocation.crmID,
						office_in_id: null,
						delivery_place_id: null,
						collection_place_id: endLocation.placeID,
						meet_and_greet_out_id: null,
						meet_and_greet_in_id: null,
					}
					// End location is office
				} else if (endLocation.crmID) {
					return {
						office_out_id: startLocation.crmID,
						office_in_id: endLocation.crmID,
						delivery_place_id: null,
						collection_place_id: null,
						meet_and_greet_out_id: null,
						meet_and_greet_in_id: null,
					}
				}
			}
		}

		return {
			office_out_id,
			office_in_id,
			delivery_place_id,
			collection_place_id,
		}
	}

	// let updateBookingPromise: Promise<IBookingModel | undefined> | null = null
	const abortController = ref<AbortController | null>(null)

	const updateBooking = async (disableCheckCode?: boolean) => {
		if (!_state.currentBookingUidCookie) return

		setIsLoading(true)
		if (abortController.value) {
			abortController.value?.abort('New request started')
			logger.log('aborted 0', abortController.value?.signal?.aborted)
		}
		abortController.value = new AbortController()

		const { signal } = abortController.value

		try {
			if (!_state.requestVariables?.dates?.start || !_state.requestVariables?.dates?.end || !_state.requestVariables?.selectedCar?.segment.id)
				return

			const isReservationStartDateCorrect = getIsStartDateCorrectForRenteon.value

			if (!isReservationStartDateCorrect) {
				const bookerStore = useBookerStore()
				setTimeout(() => {
					const { getStartDate, getEndDate } = useBookerService()
					bookerStore.setIsRenteonDateError(true)

					setBookerDates({ start: getStartDate(), end: getEndDate() }, EBookerDatesSource.AUTO)

					if (route.name?.toString().includes('reservation')) {
						bookerStore.setIsStartDateMenuOpen(true)
					}
				}, 500)

				return
			}

			const dateOut = toInstant(_state.requestVariables.dates.start)
			const dateIn = toInstant(_state.requestVariables.dates.end)

			const request: IBookingStoreRequest = {
				office_out_id: outAndInRequestLocations(_state.requestVariables).office_out_id,
				office_in_id: outAndInRequestLocations(_state.requestVariables).office_in_id,
				date_out: dateOut,
				date_in: dateIn,
				car_category_id: _state.requestVariables.selectedCar.segment.crmId,
				product_id: _state.requestVariables.selectedCar.crmID,
				booking_drivers: additionalDrivers().standardDrivers,
				services: transformServicesForRequest(_state.requestVariables.selectedServices),
				coupon_code: _state.requestVariables.couponCode ? _state.requestVariables.couponCode : null,
				delivery_place_id: outAndInRequestLocations(_state.requestVariables).delivery_place_id,
				collection_place_id: outAndInRequestLocations(_state.requestVariables).collection_place_id,
				meet_and_greet_in_id: outAndInRequestLocations(_state.requestVariables).meet_and_greet_in_id,
				meet_and_greet_out_id: outAndInRequestLocations(_state.requestVariables).meet_and_greet_out_id,
			}

			const response = await ApiService.updateBooking(request, _state.currentBookingUidCookie, locale.value, signal, _state.booking)
			_state.booking = response
			if (response?.product) {
				_state.requestVariables.selectedCar = response.product
			}

			// check if discount code is still valid
			if (!disableCheckCode && _state.requestVariables.couponCode) {
				_state.requestVariables.isCodeRechecked = true
			}

			return response
		} catch (error: any) {
			logger.log(error)
			if (signal.aborted) return _state.booking
			if (isOnReservationPage.value) {
				if (error?.errors?.product_id) {
					notyf?.error($t('error.booking_car_error'))
					await router.push(localePath({ name: 'cars' }))
				}
				notyf?.error($t('error.booking_error'))
			}
		} finally {
			if (!signal.aborted) {
				setIsLoading(false)
				if (abortController.value?.signal === signal) abortController.value = null
			}
		}
	}

	// const updateBooking = async (disableCheckCode?: boolean) => {
	// 	if (updateBookingPromise) {
	// 		abortController.abort()
	// 		updateBookingPromise = null
	// 	}
	// 	if (!_state.currentBookingUidCookie) return

	// 	updateBookingPromise = new Promise<IBookingModel | undefined>(async (resolve, reject) => {})
	// }
	// let fetchBookingPromise: Promise<IBookingModel | undefined> | null = null

	const hydrateBookerFromSession = () => {
		if (!_state.requestVariables.dates?.start || !_state.requestVariables.dates?.end) {
			const localDates = getLocalBookerDates.value
			const localStart = localDates?.start ? new Date(localDates.start) : null
			const localEnd = localDates?.end ? new Date(localDates.end) : null

			if (localStart && localEnd && isDatesCorrectForRenteon(localStart, localEnd)) {
				setBookerDates({ start: localStart, end: localEnd }, localDates.source)
			} else {
				const { getStartDate, getEndDate } = useBookerService()
				setBookerDates({ start: getStartDate(), end: getEndDate() }, EBookerDatesSource.AUTO)
			}
		}

		if (!_state.requestVariables.locations?.start) {
			const localLocations = getLocalBookerLocations.value
			if (localLocations?.start) setBookerLocations(localLocations)
		}
	}

	const fetchBooking = async () => {
		setIsLoading(true)

		try {
			_state.currentBookingUidCookie = sessionStorage.getItem(sessionStorageBookingUidToken)
			hydrateBookerFromSession()

			if (_state.currentBookingUidCookie) {
				try {
					const response = await ApiService.fetchBooking(_state.currentBookingUidCookie)

					const isReservationStartDateCorrect = getIsStartDateCorrectForRenteon.value
					const bookerStore = useBookerStore()

					_state.requestVariables.additionalDrivers = getAdditionalDriversNumberFromBooking(response)

					if (!isReservationStartDateCorrect) {
						const { getStartDate, getEndDate } = useBookerService()
						bookerStore.setIsRenteonDateError(true)

						setBookerDates({ start: getStartDate(), end: getEndDate() }, EBookerDatesSource.AUTO)

						if (route.name?.toString().includes('reservation')) {
							bookerStore.setIsStartDateMenuOpen(true)
						}
						return
					}

					if (!_state.booking) {
						_state.booking = response
						_state.requestVariables.selectedCar = response.product
						// przeterminowanych dat rezerwacji nie kopiujemy — zostają daty z bookera, które niżej wymuszą updateBooking()
						if (isDatesCorrectForRenteon(new Date(response.dateOut), new Date(response.dateIn))) {
							_state.requestVariables.dates = {
								start: response.dateOut,
								end: response.dateIn,
							}
						}
						// _state.requestVariables.locations = {
						// 	start: response.locationOut,
						// 	end: response.locationIn?.id !== response.locationOut?.id ? response.locationIn : null,
						// }

						const locationsForRequestVariables = (booking: IBookingModel) => {
							let startLocation: Partial<ILocationModel> | null = null
							let endLocation: Partial<ILocationModel> | null = null
							const deliveryPlaceId = booking.deliveryPlaceId
							const collectionPlaceId = booking.collectionPlaceId
							const meetAndGreetOut = booking.meetAndGreetOut
							const meetAndGreetIn = booking.meetAndGreetIn
							const locationIn = booking.locationIn
							const locationOut = booking.locationOut

							// startLocation
							if (deliveryPlaceId) {
								startLocation = {
									name: booking.deliveryLocation as string,
									placeID: deliveryPlaceId,
									isDelivery: true,
								}
								calculateDeliveryPrice(deliveryPlaceId, EBookerLocationType.START)
							} else if (meetAndGreetOut) {
								startLocation = meetAndGreetOut
							} else if (locationOut) {
								startLocation = locationOut
							}

							// endLocation
							if (collectionPlaceId && collectionPlaceId !== deliveryPlaceId) {
								endLocation = {
									name: booking.collectionLocation as string,
									placeID: collectionPlaceId,
									isDelivery: true,
								}
								calculateDeliveryPrice(collectionPlaceId, EBookerLocationType.END)
							} else if (meetAndGreetIn && meetAndGreetIn.id !== meetAndGreetOut?.id) {
								endLocation = meetAndGreetIn
							} else if (locationIn?.id !== locationOut?.id) {
								endLocation = locationIn
							}

							return { start: startLocation, end: endLocation }
						}

						_state.requestVariables.locations = locationsForRequestVariables(response)

						_state.requestVariables.selectedServices = response.bookingServices.filter((service) => service.isSelected)
					}
					const currency = useCookie('currency')?.value

					// check if booking variables are different, if so, update booking
					if (
						(_state.requestVariables.dates?.end && !isEqual(response.dateIn, _state.requestVariables.dates?.end)) ||
						(_state.requestVariables.dates?.start && !isEqual(response.dateOut, _state.requestVariables.dates.start)) ||
						response.product?.crmID !== _state.requestVariables.selectedCar?.crmID ||
						_state.booking.currency !== currency ||
						_state.booking?.product?.segment?.id !== _state.requestVariables?.selectedCar?.segment?.id
						// response.locationOut?.crmID !== _state.requestVariables.locations?.start?.crmID ||
						// (response.locationIn?.crmID !== response.locationOut?.crmID &&
						// 	response.locationIn?.crmID !== _state.requestVariables.locations?.end?.crmID)
					) {
						const updatedResponse = await updateBooking()
						return updatedResponse
					}

					_state.booking = response
					_state.requestVariables.selectedCar = response.product
					_state.requestVariables.selectedServices = response.bookingServices.filter((service) => service.isSelected)
					if (response.coupon) _state.requestVariables.couponCode = response.coupon.code
					return response
				} catch (error) {
					logger.log(error)
					if (isOnReservationPage.value) {
						notyf?.error($t('error.booking_error'))
					}
				}
			}

			if (!_state.requestVariables?.dates?.start || !_state.requestVariables?.dates?.end || !_state.requestVariables?.selectedCar?.segment.id)
				return
			const dateOut = toInstant(_state.requestVariables.dates.start)
			const dateIn = toInstant(_state.requestVariables.dates.end)

			try {
				const request: IBookingStoreRequest = {
					office_out_id: outAndInRequestLocations(_state.requestVariables).office_out_id,
					office_in_id: outAndInRequestLocations(_state.requestVariables).office_in_id,
					date_out: dateOut,
					date_in: dateIn,
					car_category_id: _state.requestVariables.selectedCar.segment.crmId,
					product_id: _state.requestVariables.selectedCar.crmID,
					booking_drivers: additionalDrivers().standardDrivers,
					services: transformServicesForRequest(_state.requestVariables.selectedServices),
					coupon_code: _state.requestVariables.couponCode ? _state.requestVariables.couponCode : null,
					delivery_place_id: outAndInRequestLocations(_state.requestVariables).delivery_place_id,
					collection_place_id: outAndInRequestLocations(_state.requestVariables).collection_place_id,
					meet_and_greet_in_id: outAndInRequestLocations(_state.requestVariables).meet_and_greet_in_id,
					meet_and_greet_out_id: outAndInRequestLocations(_state.requestVariables).meet_and_greet_out_id,
				}
				const response = await ApiService.createBooking(request, locale.value)
				setCurrentBookingUidCookie(response.uuid)
				_state.booking = response
				return response
			} catch (error) {
				logger.log(error)
				if (isOnReservationPage.value) {
					notyf?.error($t('error.booking_error'))
				}
			}
		} finally {
			setIsLoading(false)
		}
	}
	// 	500,
	// 	{ trailing: true },
	// )

	const finalizeBooking = async (payload: IBookingStoreFinalizeRequest) => {
		setIsLoading(true)

		const isReservationStartDateCorrect = getIsStartDateCorrectForRenteon.value
		const bookerStore = useBookerStore()

		if (!isReservationStartDateCorrect) {
			bookerStore.setIsRenteonDateError(true)
			bookerStore.setIsStartDateMenuOpen(true)
			setIsLoading(false)
			return
		}

		try {
			if (
				!_state.currentBookingUidCookie ||
				!_state.requestVariables ||
				!_state.requestVariables.dates?.start ||
				!_state.requestVariables.dates?.end ||
				!_state.requestVariables.selectedCar
			)
				return

			const request: IBookingFinalizeRequest = {
				office_out_id: outAndInRequestLocations(_state.requestVariables).office_out_id,
				office_in_id: outAndInRequestLocations(_state.requestVariables).office_in_id,
				date_out: toInstant(_state.requestVariables.dates?.start),
				date_in: toInstant(_state.requestVariables.dates?.end),
				car_category_id: _state.requestVariables.selectedCar.segment.crmId,
				product_id: _state.requestVariables.selectedCar.crmID,
				flight_number: payload.flight_number,
				client: {
					name: payload.client.name,
					surname: payload.client.surname,
					email: payload.client.email,
					phone: payload.client.phone,
					is_company: payload.client.is_company,
					company: payload.client.company,
					tax_id: payload.client.tax_id,
					street: payload.client.street,
					zip_code: payload.client.zip_code,
					city: payload.client.city,
					country_code: payload.client.country_code ? payload.client.country_code.toLocaleUpperCase() : '',
				},
				services: transformServicesForRequest(_state.requestVariables.selectedServices),
				booking_drivers: additionalDrivers().standardDrivers,
				payment_id: _state.payment.method === EPaymentMethod.ONLINE ? null : _state.payment.method,
				payment_type: _state.payment.type as EPaymentType,
				coupon_code: _state.requestVariables.couponCode ? _state.requestVariables.couponCode : null,
				delivery_place_id: outAndInRequestLocations(_state.requestVariables).delivery_place_id,
				collection_place_id: outAndInRequestLocations(_state.requestVariables).collection_place_id,
				meet_and_greet_in_id: outAndInRequestLocations(_state.requestVariables).meet_and_greet_in_id,
				meet_and_greet_out_id: outAndInRequestLocations(_state.requestVariables).meet_and_greet_out_id,
			}

			const response = await ApiService.finalizeBooking(_state.currentBookingUidCookie, request, locale.value)
			deleteCurrentBookingUidCookie()
			if (response.gateway_url) window.open(response.gateway_url, '_self')
			return response
		} catch (error) {
			logger.log(error)
			notyf?.error($t('error.booking_error'))
		} finally {
			setIsLoading(false)
		}
	}

	const selectInsurance = (insuranceId: number) => {
		// remove the current insurance from the selected services

		_state.requestVariables.selectedServices = _state.requestVariables.selectedServices?.filter((service) => !isInsuranceService(service)) || []

		// add this insurance to the selected services
		const insurance = _state.booking?.bookingServices.find((service) => service.serviceID === insuranceId)
		if (insurance) {
			_state.requestVariables.selectedServices?.push({ ...insurance, isSelected: true })
		}

		return updateBooking()
	}

	const selectService = (service: IBookingServiceModel, serviceToDel?: IBookingServiceModel) => {
		if (serviceToDel) {
			const serviceIndex = _state.requestVariables.selectedServices?.findIndex((s) => s.serviceID === serviceToDel.serviceID)
			if ((serviceIndex || serviceIndex === 0) && serviceIndex !== -1) {
				_state.requestVariables.selectedServices?.splice(serviceIndex, 1)
			}
		}

		const serviceQuantityOrBoolean = service.maximumQuantity ? 'quantity' : 'boolean'

		// if only one service can be selected from this group, remove all other services from this group

		// check if the service is already in the selected services
		const selectedService = _state.requestVariables.selectedServices?.find((s) => s.serviceID === service.serviceID)
		const selectedServiceIndex = _state.requestVariables.selectedServices?.findIndex((s) => s.serviceID === service.serviceID)

		if (serviceQuantityOrBoolean === 'boolean') {
			if (selectedService && (selectedServiceIndex || selectedServiceIndex === 0) && selectedServiceIndex !== -1) {
				// delete
				_state.requestVariables.selectedServices?.splice(selectedServiceIndex, 1)
			} else {
				_state.requestVariables.selectedServices?.push({ ...service, isSelected: true, quantity: 1 })
			}
		} else if (serviceQuantityOrBoolean === 'quantity') {
			if (selectedService && (selectedServiceIndex || selectedServiceIndex === 0)) {
				selectedService.quantity = service.quantity
				selectedService.isSelected = service.quantity > 0

				if (selectedService.quantity <= 0) {
					_state.requestVariables.selectedServices?.splice(selectedServiceIndex, 1)
				}
			} else {
				_state.requestVariables.selectedServices?.push({ ...service, isSelected: true })
			}
		}
		return updateBooking()
	}

	const calculateDeliveryPrice = async (placeID: string, key: EBookerLocationType) => {
		const bookerStore = useBookerStore()
		try {
			const location = await ApiService.calculateDeliveryPrice(placeID, key)
			if (location.origin?.price) bookerStore.setDeliveryCosts(key, location.origin?.price.amount)
		} catch (error) {
			logger.error(error)
			bookerStore.setDeliveryCosts(key, null)
		}
	}

	const unselectService = (service: IBookingServiceModel) => {
		const serviceIndex = _state.requestVariables.selectedServices?.findIndex((s) => s.serviceID === service.serviceID)
		if ((serviceIndex || serviceIndex === 0) && serviceIndex !== -1) {
			_state.requestVariables.selectedServices?.splice(serviceIndex, 1)
		}

		return updateBooking()
	}

	const removeAbroadZonesFromSelectedServices = () => {
		for (let index = _state.requestVariables.selectedServices.length - 1; index >= 0; index--) {
			if (!isAbroadZoneService(_state.requestVariables.selectedServices[index])) continue
			_state.requestVariables.selectedServices.splice(index, 1)
		}
	}

	const selectAbroadZone = (zone: IBookingServiceModel) => {
		removeAbroadZonesFromSelectedServices()
		_state.requestVariables.selectedServices.push({ ...zone, isSelected: true, quantity: 1 })

		return updateBooking()
	}

	const unselectAbroadZones = () => {
		removeAbroadZonesFromSelectedServices()

		return updateBooking()
	}

	const setIsLoading = (isLoading: boolean) => {
		_state.isLoading = isLoading
	}

	const setBookerDates = (dates: { start: Date | null; end: Date | null }, source: EBookerDatesSource = EBookerDatesSource.USER) => {
		const normalized = { start: dates?.start ?? null, end: dates?.end ?? null }
		_state.requestVariables.dates = normalized
		sessionStorage.setItem(sessionStorageBookingDatesToken, JSON.stringify({ ...normalized, source }))
	}

	const getLocalBookerDates = computed(() => {
		const dates = sessionStorage.getItem(sessionStorageBookingDatesToken)
		if (dates) {
			const parsed = JSON.parse(dates)
			return { ...parsed, source: parsed?.source ?? EBookerDatesSource.USER }
		}
		return null
	})

	const setBookerLocations = (locations: { start: ILocationModel | null; end: ILocationModel | null }) => {
		const normalized = { start: locations?.start ?? null, end: locations?.end ?? null }
		_state.requestVariables.locations = normalized
		sessionStorage.setItem(sessionStorageBookingLocationsToken, JSON.stringify(normalized))
	}

	const getLocalBookerLocations = computed(() => {
		const locations = sessionStorage.getItem(sessionStorageBookingLocationsToken)
		if (locations) {
			return JSON.parse(locations)
		}
		return null
	})

	const setOtherEndLocation = (value: boolean) => {
		_state.requestVariables.otherEndLocation = value
	}

	const setCurrentBookingUidCookie = (uid: string) => {
		_state.currentBookingUidCookie = uid
		sessionStorage.setItem(sessionStorageBookingUidToken, uid)
	}

	const deleteCurrentBookingUidCookie = () => {
		_state.currentBookingUidCookie = null
		sessionStorage.removeItem(sessionStorageBookingUidToken)
		sessionStorage.removeItem(sessionStorageBookingLocationsToken)
		sessionStorage.removeItem(sessionStorageBookingDatesToken)
	}

	const updateSelectedCar = (car: IProductModel | null) => {
		// remove current insurance from selected services
		_state.requestVariables.selectedServices = _state.requestVariables.selectedServices?.filter((service) => !isInsuranceService(service))

		_state.requestVariables.selectedCar = car

		// delete insurance from booking services
		const insuranceService = _state.booking?.bookingServices.find(isInsuranceService)
		if (insuranceService) {
			insuranceService.isSelected = false
			insuranceService.quantity = 0
		}
	}

	const updatePayment = (payment: IReservationPaymentModel) => {
		_state.payment = payment
	}

	const updatePaymentType = (type: EPaymentType) => {
		_state.payment.type = type
	}

	const updatePaymentMethod = (method: EPaymentMethod | null) => {
		_state.payment.method = method
	}

	const setDiscountCode = (code: ICouponCodeModel | null, recheck?: boolean, disableCheckCode?: boolean) => {
		if (_state.booking) {
			_state.requestVariables.couponCode = code?.code as string | null
			// _state.booking.coupon = code
		}
		if (!recheck) updateBooking(disableCheckCode)
	}

	const additionalDrivers = () => {
		const additionalDriversServiceId = findServiceByCode(_state.booking?.bookingServices, ERenteonServiceCode.ADDITIONAL_DRIVER)?.serviceID

		const quantity = _state.requestVariables.additionalDrivers

		const standardDrivers: { name: string }[] = [
			{
				name: 'Main Driver',
			},
		]
		let additionalDriversService: Partial<IBookingServiceModel> | null = {
			serviceID: additionalDriversServiceId,
			quantity: 0,
			isSelected: false,
		}

		if (quantity < 4) {
			for (let i = 0; i < quantity; i++) {
				standardDrivers.push({ name: 'Driver' })
			}
		} else {
			for (let i = 0; i < 3; i++) {
				standardDrivers.push({ name: 'Driver' })
			}

			additionalDriversService.quantity = quantity - 3
			additionalDriversService.isSelected = true
		}

		if (!additionalDriversService.serviceID) {
			additionalDriversService = null
		}

		return { standardDrivers, additionalDriversService }
	}

	const setAdditionalDrivers = (additionalDriversQuantity: number) => {
		_state.requestVariables.additionalDrivers = additionalDriversQuantity
		return updateBooking()
	}

	const getAdditionalDriverService = computed(() => {
		const firstService = findServiceByCode(_state.booking?.bookingServices, ERenteonServiceCode.ADDITIONAL_DRIVER_FEE)
		const secondService = findServiceByCode(_state.booking?.bookingServices, ERenteonServiceCode.ADDITIONAL_DRIVER)

		// adding points from ADD service to ADF service because ADD service is not visible in the booking
		let finalService: IBookingServiceModel | undefined
		if (
			firstService &&
			firstService.isSelected &&
			firstService.quantity > 1 &&
			firstService.pendingPoints &&
			secondService &&
			secondService.pendingPoints
		) {
			finalService = {
				...secondService,
				pendingPoints:
					_state.requestVariables.additionalDrivers <= 3
						? firstService.pendingPoints
						: firstService.pendingPoints + secondService.pendingPoints,
			}
		} else {
			return secondService
		}

		return finalService
	})

	const getAdditionalDriversNumberFromBooking = (booking: IBookingModel) => {
		// get booking_drivers length - 1 (main driver) and add quantity from additionalDriversService

		const bookingDriversLength = booking.bookingDrivers.length - 1
		const additionalDriversService = findServiceByCode(booking.bookingServices, ERenteonServiceCode.ADDITIONAL_DRIVER)
		const additionalDriversQuantity = additionalDriversService?.isSelected ? additionalDriversService?.quantity : 0

		return bookingDriversLength + (additionalDriversQuantity || 0)
	}

	const getIsStartDateCorrectForRenteon = computed(() => {
		const { getStartDate } = useBookerService()
		const minimalStartDate = getStartDate()

		if (!_state.requestVariables.dates?.start) return false
		if (isAfter(new Date(minimalStartDate), new Date(_state.requestVariables.dates.start))) return false
		return true
	})

	const isDatesCorrectForRenteon = (startDate: Date, endDate: Date) => {
		const { getStartDate } = useBookerService()
		const minimalStartDate = getStartDate()

		if (!startDate || !endDate) return false
		if (isAfter(new Date(minimalStartDate), new Date(startDate))) return false
		if (isAfter(new Date(startDate), new Date(endDate))) return false
		return true
	}

	const getIsLoading = computed(() => {
		return _state.isLoading
	})

	const getAdditionalDrivers = computed(() => {
		return _state.requestVariables.additionalDrivers
	})

	const getBooking = computed(() => {
		return _state.booking
	})

	const getBookingCookie = computed(() => {
		return _state.currentBookingUidCookie
	})

	const getBookerDates = computed(() => {
		return _state.requestVariables.dates
	})

	const getBookerLocations = computed(() => {
		return _state.requestVariables.locations
	})

	const getIsOtherEndLocation = computed(() => {
		return _state.requestVariables.otherEndLocation
	})

	const getBookingInsurances = computed(() => {
		return pickInsuranceServices(_state.booking?.bookingServices)
	})

	const getSelectedCar = computed(() => {
		return _state.requestVariables.selectedCar
	})

	const getSelectedInsurance = computed(() => {
		return _state.requestVariables.selectedServices?.find(isInsuranceService)
	})

	const getDays = computed(() => {
		if (!_state.requestVariables.dates?.start || !_state.requestVariables.dates?.end) return null
		return getRentalDays(_state.requestVariables.dates.start, _state.requestVariables.dates.end)
	})

	const getAbroadZoneServices = computed(() => pickAbroadZoneServices(_state.booking?.bookingServices))

	const getAbroadZoneSelectedServices = computed(() => pickAbroadZoneServices(_state.requestVariables.selectedServices))

	const getAbroadZoneSelectedService = computed(() => {
		return getAbroadZoneSelectedServices.value[0]
	})

	const getAddonServices = computed(() => pickAddonServices(_state.booking?.bookingServices))

	const getCarRentCost = computed(() => getCarRentAmount(_state.booking?.bookingServices))

	const getAdditionalServicesCost = computed(() => getAdditionalServicesAmount(_state.booking?.bookingServices))

	const getPrepareCost = computed(() => findServiceByCode(_state.booking?.bookingServices, ERenteonServiceCode.PREPARATION_FEE)?.amountTotal)

	const getPayment = computed(() => {
		return _state.payment
	})

	const getPaymentType = computed(() => {
		return _state.payment.type
	})

	const getPaymentMethod = computed(() => {
		return _state.payment.method
	})

	const getRequestVariables = computed(() => {
		return _state.requestVariables
	})

	const setIsCodeRechecked = (state: boolean = false) => {
		_state.requestVariables.isCodeRechecked = state
	}

	const getIsCodeRechecked = computed(() => {
		return _state.requestVariables.isCodeRechecked
	})

	return {
		state: _state,
		fetchBooking,
		updateBooking,
		setIsLoading,
		setBookerDates,
		setBookerLocations,
		setOtherEndLocation,
		setAdditionalDrivers,
		updateSelectedCar,
		selectInsurance,
		selectService,
		unselectService,
		updatePayment,
		updatePaymentType,
		updatePaymentMethod,
		finalizeBooking,
		deleteCurrentBookingUidCookie,
		calculateDeliveryPrice,
		setDiscountCode,
		getIsStartDateCorrectForRenteon,
		getDays,
		getLocalBookerDates,
		isDatesCorrectForRenteon,
		getLocalBookerLocations,
		getBooking,
		getBookerDates,
		getAdditionalDriverService,
		getBookingCookie,
		getAdditionalDrivers,
		getBookerLocations,
		getIsOtherEndLocation,
		getBookingInsurances,
		getSelectedInsurance,
		getSelectedCar,
		getAddonServices,
		getAbroadZoneServices,
		getAbroadZoneSelectedService,
		getAbroadZoneSelectedServices,
		selectAbroadZone,
		unselectAbroadZones,
		getIsLoading,
		getCarRentCost,
		getAdditionalServicesCost,
		getPrepareCost,
		getPayment,
		getPaymentType,
		getPaymentMethod,
		getRequestVariables,
		setIsCodeRechecked,
		getIsCodeRechecked,
	}
})
